import _jsx from "@babel/runtime-corejs3/helpers/esm/jsx";
var _IconWarningSolid, _Flex$Item, _Flex$Item2;
/*
 * Copyright (C) 2019 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React from 'react';
import { bool, func, number, shape, string, object } from 'prop-types';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { FormFieldGroup } from '@instructure/ui-form-field';
import { IconLockLine, IconWarningSolid } from '@instructure/ui-icons';
import { Flex } from '@instructure/ui-flex';
import { Text } from '@instructure/ui-text';
import { RadioInput, RadioInputGroup } from '@instructure/ui-radio-input';
import formatMessage from '../../../../format-message';
import DimensionInput from './DimensionInput';
export { default as useDimensionsState } from './useDimensionsState';
const errorMessage = message => {
  return /*#__PURE__*/_jsx(Flex, {
    gap: "xx-small",
    alignItems: "center"
  }, void 0, _IconWarningSolid || (_IconWarningSolid = /*#__PURE__*/_jsx(IconWarningSolid, {
    color: "error"
  })), /*#__PURE__*/_jsx(Text, {
    color: "danger",
    size: "small"
  }, void 0, message));
};
const getMessage = (dimensionsState, minWidth, minHeight, minPercentage) => {
  let result = {
    text: formatMessage('Aspect ratio will be preserved'),
    type: 'hint'
  };
  if (dimensionsState.usePercentageUnits) {
    if (!dimensionsState.isNumeric) {
      result = {
        text: formatMessage('Percentage must be a number'),
        type: 'error'
      };
    } else if (!dimensionsState.isAtLeastMinimums) {
      result = {
        text: formatMessage('Percentage must be at least {percentage}%', {
          percentage: minPercentage
        }),
        type: 'error'
      };
    }
  } else if (!dimensionsState.isNumeric) {
    result = {
      text: formatMessage('Width and height must be numbers'),
      type: 'error'
    };
  } else if (!dimensionsState.isAtLeastMinimums) {
    result = {
      text: formatMessage('Pixels must be at least {width} x {height}px', {
        width: minWidth,
        height: minHeight
      }),
      type: 'error'
    };
  }
  return result;
};
export default function DimensionsInput(props) {
  const {
    dimensionsState,
    minHeight,
    minWidth,
    minPercentage,
    hidePercentage,
    dimensionsRef
  } = props;
  const message = getMessage(dimensionsState, minWidth, minHeight, minPercentage);
  const secondaryMessage = {
    ...message,
    text: ''
  };
  return /*#__PURE__*/_jsx(Flex, {
    direction: "column"
  }, void 0, /*#__PURE__*/_jsx(Flex.Item, {
    padding: "small"
  }, void 0, hidePercentage ? /*#__PURE__*/_jsx(Text, {
    weight: "bold"
  }, void 0, formatMessage('Custom width and height (Pixels)')) : /*#__PURE__*/_jsx(RadioInputGroup, {
    "data-testid": "dimension-type",
    name: "dimension-type",
    description: formatMessage('Dimension Type'),
    onChange: e => {
      dimensionsState.setUsePercentageUnits(e.target.value === 'percentage');
    },
    value: dimensionsState.usePercentageUnits ? 'percentage' : 'pixels'
  }, void 0, /*#__PURE__*/_jsx(RadioInput, {
    label: formatMessage('Percentage'),
    value: "percentage"
  }), /*#__PURE__*/_jsx(RadioInput, {
    label: formatMessage('Pixels'),
    value: "pixels"
  }))), /*#__PURE__*/_jsx(Flex.Item, {
    padding: "small"
  }, void 0, /*#__PURE__*/_jsx(FormFieldGroup, {
    description: /*#__PURE__*/_jsx(ScreenReaderContent, {}, void 0, formatMessage('Dimensions'))
  }, void 0, /*#__PURE__*/_jsx(Flex, {
    alignItems: "start",
    direction: "row",
    "data-testid": "input-number-container"
  }, void 0, dimensionsState.usePercentageUnits ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(Flex.Item, {
    shouldShrink: true,
    shouldGrow: true
  }, void 0, /*#__PURE__*/_jsx(DimensionInput, {
    dimensionState: dimensionsState.percentageState,
    label: formatMessage('Percentage'),
    messages: [secondaryMessage],
    dimensionsRef: dimensionsRef
  })), _Flex$Item || (_Flex$Item = /*#__PURE__*/_jsx(Flex.Item, {
    padding: "x-small small"
  }, void 0, "%"))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(Flex.Item, {
    shouldShrink: true
  }, void 0, /*#__PURE__*/_jsx(DimensionInput, {
    dimensionState: dimensionsState.widthState,
    label: formatMessage('Width'),
    minValue: minWidth,
    messages: [secondaryMessage],
    dimensionsRef: dimensionsRef
  })), _Flex$Item2 || (_Flex$Item2 = /*#__PURE__*/_jsx(Flex.Item, {
    padding: "x-small small"
  }, void 0, /*#__PURE__*/_jsx(IconLockLine, {}))), /*#__PURE__*/_jsx(Flex.Item, {
    shouldShrink: true
  }, void 0, /*#__PURE__*/_jsx(DimensionInput, {
    dimensionState: dimensionsState.heightState,
    label: formatMessage('Height'),
    minValue: minHeight,
    messages: [secondaryMessage]
  }))))), (message => {
    if (!message) {
      return;
    }
    return /*#__PURE__*/_jsx("div", {
      "data-testid": "message"
    }, void 0, message.type === 'error' ? errorMessage(message.text) : /*#__PURE__*/_jsx(Text, {
      size: "small"
    }, void 0, message.text));
  })(message)));
}
DimensionsInput.defaultProps = {
  hidePercentage: false
};